// src/components/OpenNotesList.jsx
//
// The "Notes" tab of Recommendation and Escalations (doctor and navigator portals): open
// action items across patients that are NOT part of an escalation. The backend
// (GET /doctor/action-items/open) already drops the triage/escalation-generated
// ones and scopes by role, so this is just the list. Each row opens the
// patient, where the item is completed.

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doctorActionItemAPI } from '../services/doctorApi';
import Icon from './Icon';
import { matchesQuery } from '../utils/searchMatch';

const PRIORITY_BADGE = {
  URGENT:  { cls: 'badge-critical', label: 'Urgent' },
  HIGH:    { cls: 'badge-high',     label: 'High' },
  MEDIUM:  { cls: 'badge-moderate', label: 'Medium' },
  LOW:     { cls: 'badge-low',      label: 'Low' },
  ROUTINE: { cls: 'badge-unknown',  label: 'Routine' },
};

// Exported so the page's priority filter lists the same levels, in this order.
export const PRIORITY_OPTIONS = Object.entries(PRIORITY_BADGE).map(([value, b]) => ({ value, label: b.label }));

function formatDue(date) {
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// patientPath: (patientId) => route of that patient in the current portal.
export default function OpenNotesList({ patientPath, onLoaded, query = '', priority = 'ALL' }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await doctorActionItemAPI.listOpen();
      const rows = Array.isArray(res.data) ? res.data : [];
      setItems(rows);
      if (onLoaded) onLoaded(rows.length);
    } catch {
      setError('Could not load notes. Please try again.');
      setItems([]);
    }
  }, [onLoaded]);

  useEffect(() => { load(); }, [load]);

  if (items === null) {
    return (
      <div className="skeleton-list">
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton skeleton--row" />)}
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div className="alert alert-error" role="alert">{error}</div>
        <button type="button" className="btn btn-ghost" onClick={() => { setItems(null); load(); }}>
          Retry
        </button>
      </>
    );
  }

  if (items.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="note" size={20} /></div>
        <div className="empty-title">No open notes</div>
        <div className="empty-desc">
          Open items that aren&rsquo;t part of an escalation will show up here.
        </div>
      </div>
    );
  }

  const shown = items.filter(i => (priority === 'ALL' || i.priority === priority) && matchesQuery(query, i.description, i.patientName, PRIORITY_BADGE[i.priority]?.label));
  if (shown.length === 0) {
    return <div className="card-row-meta">No notes match your search or filter.</div>;
  }

  return (
    <div className="card-list">
      {shown.map(item => {
        const badge = PRIORITY_BADGE[item.priority] || PRIORITY_BADGE.ROUTINE;
        return (
          // The spans only give the phone layout (.note-row in index.css)
          // something to place: on desktop they render exactly as the plain
          // "description / name · due date" row did. The due date is there
          // twice — inline for desktop, its own line for phones — and CSS
          // shows exactly one, so the desktop text run is unchanged.
          <button
            key={item.id}
            type="button"
            className="card-row note-row"
            data-testid="note-row-link"
            onClick={() => navigate(patientPath(item.patientId))}
          >
            <div className="card-row-body">
              <div className="card-row-title note-row-text" title={item.description}>{item.description}</div>
              <div className="card-row-meta">
                <span className="note-row-name">{item.patientName || 'Patient'}</span>
                {item.dueDate && <span className="note-row-due-inline">{` · due ${formatDue(item.dueDate)}`}</span>}
              </div>
            </div>
            {item.dueDate && <span className="note-row-due">Due {formatDue(item.dueDate)}</span>}
            <span className={`badge ${badge.cls} note-row-chip`}>{badge.label}</span>
            <Icon name="chevronRight" size={14} className="note-row-chevron" />
          </button>
        );
      })}
    </div>
  );
}
